import React, { useState } from 'react';
import styles from './productTable.module.css';
import { useNavigate } from 'react-router-dom';
import { Uparrow, Downarrow, Leaf, Pdf } from '../../Svg';
import { toast } from 'react-toastify';
import { stripHtmlTags } from '../../utils/fomateDate';

const firstOf = (value) => (Array.isArray(value) ? value[0] : value);

// Search results for a new schedule. Same look as before, without the
// attribute columns, so the table fits the page with no sideways scroll.
const ProductTable = ({ products, handleAddtoSchedule, selectedIds = [] }) => {
    const [quantities, setQuantities] = useState({});
    const [expandedProducts, setExpandedProducts] = useState({});
    const selected = new Set(selectedIds.map(String));

    const navigate = useNavigate();

    const toggleShowAll = (productId) => {
        setExpandedProducts((prev) => ({
            ...prev,
            [productId]: !prev[productId],
        }));
    };

    const handleQuantityChange = (productId, value) => {
        setQuantities(prev => ({
            ...prev,
            [productId]: value
        }));
    };

    const handleAddClick = (productId, product) => {
        const quantity = quantities[productId] || '';
        if (!quantity) {
            toast.error('Please enter a quantity');
            return;
        }
        handleAddtoSchedule(productId, quantity, product);
    };

    const headerCell = (label) => (
        <div className={styles.headerWithFilter}>
            <span>{label}</span>
            <div className={styles.sortIcons}>
                <Uparrow />
                <Downarrow />
            </div>
        </div>
    );

    return (
        <div style={{ width: '100%', margin: '12px 0', border: '1px solid #d9d9d9', borderRadius: 8, overflow: 'hidden' }}>
            <table className={`${styles.productTable} ${styles.compactTable}`}>
                <colgroup>
                    <col style={{ width: '5%' }} />
                    <col style={{ width: '6%' }} />
                    <col style={{ width: '31%' }} />
                    <col style={{ width: '11%' }} />
                    <col style={{ width: '11%' }} />
                    <col style={{ width: '8%' }} />
                    <col style={{ width: '8%' }} />
                    <col style={{ width: '6%' }} />
                    <col style={{ width: '14%' }} />
                </colgroup>
                <thead className={styles.productTableHead}>
                    <tr>
                        <th>Compare</th>
                        <th>Data sheet</th>
                        <th>{headerCell('Part')}</th>
                        <th>{headerCell('Manufacturer')}</th>
                        <th>{headerCell('Available Stock')}</th>
                        <th>Unit Price</th>
                        <th>HSN Code</th>
                        <th>SPQ</th>
                        <th>Required Quantity</th>
                    </tr>
                </thead>
                <tbody>
                    {products?.map((product, index) => {
                        const minMultiplier = product?.inventory?.minimumMultiplier || 1;
                        const quantity = quantities[product._id] || '';
                        const image = firstOf(product?.assets?.featuredImage) || firstOf(product?.assets?.image);
                        const datasheet = firstOf(product?.productOverview?.datasheetUrl);
                        const isAdded = selected.has(String(product._id));
                        const priceRows = Array.isArray(product?.quantityTable) ? product.quantityTable : [];
                        const extra = (key) => {
                            const v = product?.additionalResources?.[key] ?? product?.attributes?.[key] ?? product?.environmental?.[key];
                            return [].concat(v ?? []).filter((x) => x !== '' && x !== null && x !== undefined).join(', ') || '—';
                        };
                        const hsnCode = extra('HSN Code');
                        const packQty = product?.additionalResources?.Standard_Packaging_Quantity
                            ? extra('Standard_Packaging_Quantity')
                            : String(product?.inventory?.minimumMultiplier || '—');

                        return (
                            <tr key={product._id || index} style={isAdded ? { background: '#FBFAFF' } : undefined}>
                                <td className={styles.tdBorder}>
                                    <input type="checkbox" />
                                </td>
                                <td className={styles.tdBorder}>
                                    <div className={styles.dataCol}>
                                        {datasheet ? (
                                            <a href={datasheet} target="_blank" rel="noopener noreferrer" title="Datasheet"><Pdf /></a>
                                        ) : (
                                            <Pdf />
                                        )}
                                        <Leaf />
                                    </div>
                                </td>
                                <td className={styles.tdBorder}>
                                    <div className={styles.dataRow} style={{ minWidth: 0 }}>
                                        {image ? (
                                            <img src={image} alt="" style={{ width: 50, height: 50, objectFit: 'contain', flexShrink: 0 }} />
                                        ) : (
                                            <div style={{ width: 50, height: 50, borderRadius: 6, background: '#F2F4F7', flexShrink: 0 }} />
                                        )}
                                        <div className={styles.dataCol} style={{ minWidth: 0, flex: 1, overflow: 'hidden' }}>
                                            <div className={styles.dataColorChange} onClick={() => navigate(`/productCode/${product?._id}`)} style={{ cursor: 'pointer', overflowWrap: 'anywhere' }}>
                                                {firstOf(product?.productOverview?.productNumber)}
                                            </div>
                                            <div style={{ overflowWrap: 'anywhere', whiteSpace: 'normal' }}>{stripHtmlTags(firstOf(product?.productOverview?.description) || product?.description || '')}</div>
                                        </div>
                                    </div>
                                </td>
                                <td className={styles.tdBorder}>
                                    <div className={styles.dataColorChange} style={{ wordBreak: 'break-word' }}>
                                        {firstOf(product?.productOverview?.manufacturer)?.name || ''}
                                    </div>
                                </td>
                                <td className={styles.tdBorder}>
                                    {product?.inventory?.qtyAvailable ?? 0}
                                    <div className={styles.dataMargin}>in stock</div>
                                    {product?.inventory?.factoryStock && (
                                        <>
                                            {product?.inventory?.factoryStock}
                                            <div className={styles.dataMargin}>factoryStock</div>
                                        </>
                                    )}
                                    <div>CanShip Immediately: {product?.inventory?.canShipImmediately ? 'YES' : 'NO'}</div>
                                </td>
                                <td className={styles.tdBorder}>
                                    {priceRows
                                        .slice(0, expandedProducts[product?._id] ? priceRows.length : 2)
                                        .map((i, idx) => (
                                            <div key={idx} className={styles.dataMargin}>
                                                {i?.breakQty}: <span style={{ color: '#222222', fontWeight: 700 }}>₹{i?.unitPrice}</span>
                                                <div>{i?.packaging}</div>
                                            </div>
                                        ))}
                                    {priceRows.length > 2 && (
                                        <a
                                            href="#"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                toggleShowAll(product?._id);
                                            }}
                                        >
                                            <div className={styles.dataColorChange}>
                                                {expandedProducts[product?._id] ? "Show Less" : "More Inventory"}
                                            </div>
                                        </a>
                                    )}
                                </td>
                                <td className={styles.tdBorder} style={{ overflowWrap: 'anywhere' }}>{hsnCode}</td>
                                <td className={styles.tdBorder}>{packQty}</td>
                                <td className={styles.tdBorder}>
                                    <input
                                        type="number"
                                        placeholder="Enter qty"
                                        className={styles.dataInput}
                                        style={{ width: '100%', boxSizing: 'border-box' }}
                                        value={quantity}
                                        onChange={(e) => handleQuantityChange(product._id, e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                handleAddClick(product._id, product);
                                            }
                                        }}
                                        min="1"
                                        step={minMultiplier}
                                    />
                                    <div className={styles.dataMargin}>Min {minMultiplier} · SPQ {minMultiplier}</div>
                                    <div className={styles.dataCol}>
                                        <button
                                            type="button"
                                            className={styles.scheduleButton}
                                            disabled={!quantity || quantity <= 0}
                                            onClick={() => handleAddClick(product._id, product)}
                                            style={{
                                                cursor: 'pointer',
                                                color: (!quantity || quantity <= 0) ? '#a284e6' : '#512DA8'
                                            }}
                                        >
                                            {isAdded ? '✓ Update schedule' : '+ Add to schedule'}
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

export default ProductTable;
